import { create } from 'zustand';
import { getSupportTickets } from '../api/generated/support-tickets/support-tickets';
import { getPublicSupport } from '../api/generated/public-support/public-support';
import { useToastStore } from './useToastStore';
import { showApiError } from '../errors';
import i18n from '@/i18n/config';
import type {
  CreateSupportTicketRequest,
  SupportTicketDto,
  SupportTicketStatus,
  SupportTicketType,
} from '../api/types/admin';

const {
  getApiV1SupportTickets,
  getApiV1SupportTicketsId,
  postApiV1SupportTickets,
  patchApiV1SupportTicketsIdStatus,
  postApiV1SupportTicketsIdReplies,
} = getSupportTickets();

const { postApiV1PublicSupportTickets } = getPublicSupport();

const t = (key: string) => i18n.t(key);

export interface SupportFilters {
  status?: SupportTicketStatus;
  type?: SupportTicketType;
  tenantId?: string;
}

interface SupportState {
  tickets: SupportTicketDto[];
  filters: SupportFilters;
  loading: boolean;
  saving: boolean;
  error: string | null;

  setFilters: (f: Partial<SupportFilters>) => void;
  fetchAll: () => Promise<void>;
  getById: (id: string) => Promise<SupportTicketDto | null>;
  create: (
    req: CreateSupportTicketRequest,
    isPublic: boolean,
  ) => Promise<string | null>;
  reply: (id: string, body: string) => Promise<boolean>;
  setStatus: (id: string, status: SupportTicketStatus) => Promise<boolean>;
}

export const useSupportStore = create<SupportState>((set, get) => ({
  tickets: [],
  filters: {},
  loading: false,
  saving: false,
  error: null,

  setFilters: (f) => {
    set((s) => ({ filters: { ...s.filters, ...f } }));
  },

  fetchAll: async () => {
    set({ loading: true, error: null });
    try {
      const { status, type, tenantId } = get().filters;
      const response = await getApiV1SupportTickets({
        status,
        type,
        tenantId: tenantId || undefined,
      });
      const sorted = [...(response ?? [])].sort((a, b) => {
        const da = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const db = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return db - da;
      });
      set({ tickets: sorted, loading: false });
    } catch (error) {
      const apiError = showApiError(error);
      set({ error: apiError.code, loading: false });
    }
  },

  getById: async (id) => {
    try {
      return await getApiV1SupportTicketsId(id);
    } catch (error) {
      showApiError(error);
      return null;
    }
  },

  create: async (req, isPublic) => {
    set({ saving: true });
    try {
      const created = isPublic
        ? await postApiV1PublicSupportTickets(req)
        : await postApiV1SupportTickets(req);
      set({ saving: false });
      return created?.id ?? null;
    } catch (error) {
      showApiError(error);
      set({ saving: false });
      return null;
    }
  },

  reply: async (id, body) => {
    set({ saving: true });
    try {
      const updated = await postApiV1SupportTicketsIdReplies(id, { body });
      set((s) => ({
        tickets: s.tickets.map((t0) => (t0.id === id ? { ...t0, ...updated } : t0)),
        saving: false,
      }));
      useToastStore.getState().addToast(t('admin:support.messages.replySent'), 'success');
      return true;
    } catch (error) {
      showApiError(error);
      set({ saving: false });
      return false;
    }
  },

  setStatus: async (id, status) => {
    set({ saving: true });
    try {
      const updated = await patchApiV1SupportTicketsIdStatus(id, { status });
      set((s) => ({
        tickets: s.tickets.map((t0) => (t0.id === id ? { ...t0, ...updated } : t0)),
        saving: false,
      }));
      useToastStore.getState().addToast(t('admin:support.messages.statusChanged'), 'success');
      return true;
    } catch (error) {
      showApiError(error);
      set({ saving: false });
      return false;
    }
  },
}));
